import React, { useState, useMemo, useEffect, useRef, Suspense, lazy } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Minus, Loader2, Sparkles } from 'lucide-react';
import api from '../../services/api';

const AssistantChat = lazy(() => import('./AssistantChat'));

const NOVA_AVATAR = '/nova-avatar.jpg';

const SETUP_WELCOME = {
  role: 'assistant',
  content:
    "Bonjour ! Je vois que votre abonnement est actif mais que sa configuration ne semble pas encore terminée. Sur quel appareil souhaitez-vous regarder votre IPTV ?",
  quick_replies: [
    'Fire TV Stick', 'Android TV / Google TV', 'Télévision Samsung',
    'Télévision LG', 'Smartphone ou tablette', 'Ordinateur', 'Autre appareil',
  ],
};

// Routes où Nova est déjà présente en plein écran / interfaces d'administration.
const HIDDEN_PREFIXES = ['/admin', '/maintenance', '/influencer', '/assistance', '/tutoriels'];
// Pages où l'invitation commerciale proactive n'a pas de sens (paiement/auth en cours).
const PROACTIVE_EXCLUDED_PREFIXES = ['/paiement', '/payment', '/inscription', '/connexion'];
const PROACTIVE_DELAY_MS = 30000;

const derivePageContext = (path) => {
  let page_type = 'autre';
  if (path === '/') page_type = 'accueil';
  else if (path.startsWith('/offre')) page_type = 'offres';
  else if (path.startsWith('/paiement') || path.startsWith('/payment') || path.includes('payment-success')) page_type = 'paiement';
  else if (path.startsWith('/client')) page_type = 'espace_client';
  else if (path.startsWith('/vendor') || path.startsWith('/vendeur')) page_type = 'espace_vendeur';
  else if (path.startsWith('/settings')) page_type = 'compte';
  else if (path.startsWith('/telechargements')) page_type = 'telechargements';
  else if (path.startsWith('/faq')) page_type = 'faq';
  else if (path.startsWith('/inscription') || path.startsWith('/connexion')) page_type = 'authentification';
  return { route: path, page_type };
};

export const NovaFloatingWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false); // reste monté après réduction (conserve la conversation)
  const [resumeKey, setResumeKey] = useState(0);
  const [setupMode, setSetupMode] = useState(false);
  const [setupPrompt, setSetupPrompt] = useState(null); // {subscription_id, subscription_name, first_name, campaign?, campaign_intro?}
  const [promptChecked, setPromptChecked] = useState(false);
  const [campaignSource, setCampaignSource] = useState(null); // ex: 'waiting_7d'
  const [showProactiveInvite, setShowProactiveInvite] = useState(false);

  const hidden = HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));
  const pageContext = useMemo(() => derivePageContext(location.pathname), [location.pathname]);

  const isLoggedIn = !!localStorage.getItem('auth_token');
  const proactiveExcluded = PROACTIVE_EXCLUDED_PREFIXES.some((p) => location.pathname.startsWith(p));

  // Invitation commerciale proactive — visiteur (non connecté) inactif ~30s.
  // Affichée UNE SEULE FOIS par session, jamais si le widget est déjà ouvert
  // ou si une autre notification proactive (setup) est déjà visible.
  const openRef = useRef(open);
  const setupPromptRef = useRef(setupPrompt);
  openRef.current = open;
  setupPromptRef.current = setupPrompt;
  useEffect(() => {
    if (hidden || isLoggedIn || proactiveExcluded) return;
    if (sessionStorage.getItem('nova_proactive_shown')) return;
    const t = setTimeout(() => {
      if (!openRef.current && !setupPromptRef.current) {
        setShowProactiveInvite(true);
        sessionStorage.setItem('nova_proactive_shown', '1');
      }
    }, PROACTIVE_DELAY_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden, isLoggedIn, proactiveExcluded]);
  const pendingIntent = (open || mounted) ? sessionStorage.getItem('nova_pending_intent') : null;
  const initialQuestion = (isLoggedIn && pendingIntent) ? pendingIntent : '';

  // Notification proactive « installation non terminée » — une seule fois, avec temporisation.
  useEffect(() => {
    if (hidden || !isLoggedIn || promptChecked || open) return;
    const t = setTimeout(async () => {
      setPromptChecked(true);
      try {
        const r = await api.get('/assistant/setup-status');
        const d = r.data || {};
        if (d.show_prompt && d.subscription_id) {
          const dismissed = sessionStorage.getItem(`nova_setup_dismissed_${d.subscription_id}`);
          if (!dismissed) setSetupPrompt(d);
        }
      } catch (e) { /* silencieux */ }
    }, 4000);
    return () => clearTimeout(t);
  }, [hidden, isLoggedIn, promptChecked, open]);

  useEffect(() => {
    if ((open || setupPrompt) && showProactiveInvite) setShowProactiveInvite(false);
  }, [open, setupPrompt, showProactiveInvite]);

  if (hidden) return null;

  const emitCampaignEvent = (event) => {
    const camp = setupPrompt?.campaign || campaignSource;
    if (!camp) return;
    api.post('/assistant/campaign-event', {
      event, campaign: camp, subscription_id: setupPrompt?.subscription_id,
    }).catch(() => {});
  };

  const openWidget = (setup = false) => {
    if (isLoggedIn && sessionStorage.getItem('nova_pending_intent')) {
      setResumeKey((k) => k + 1);
    }
    if (setup) {
      setSetupMode(true);
      setResumeKey((k) => k + 1);
      if (setupPrompt?.campaign) {
        setCampaignSource(setupPrompt.campaign);
        sessionStorage.setItem('nova_campaign_source', setupPrompt.campaign);
        emitCampaignEvent('opened_nova');
      }
    }
    setMounted(true);
    setOpen(true);
    setSetupPrompt(null);
  };

  const minimize = () => setOpen(false); // conserve la conversation
  const close = () => { setOpen(false); setMounted(false); setSetupMode(false); }; // démonte → masque les secrets

  const handleAfterNavigate = () => { setOpen(false); };

  const dismissPrompt = (reason) => {
    const sub = setupPrompt?.subscription_id;
    setSetupPrompt(null);
    if (sub) sessionStorage.setItem(`nova_setup_dismissed_${sub}`, '1');
    api.post('/assistant/setup-status/dismiss', { subscription_id: sub, reason }).catch(() => {});
  };

  const dismissProactiveInvite = () => setShowProactiveInvite(false);

  const openFromProactiveInvite = () => {
    setShowProactiveInvite(false);
    openWidget(false);
  };

  return (
    <>
      {/* Invitation commerciale proactive — visiteur inactif ~30s (une fois/session) */}
      {!open && showProactiveInvite && !setupPrompt && (
        <div
          className="fixed bottom-20 right-4 left-4 sm:left-auto sm:right-5 z-[60] sm:w-[320px] rounded-2xl bg-[#121214] border border-[#00FFFF]/30 shadow-xl shadow-black/40 p-4 animate-in fade-in slide-in-from-bottom-3 duration-500"
          data-testid="nova-proactive-invite"
        >
          <div className="flex items-start gap-2 mb-2">
            <img src={NOVA_AVATAR} alt="Nova" className="w-7 h-7 rounded-full object-cover border border-[#00FFFF]/40 flex-shrink-0" />
            <p className="text-sm text-white font-semibold flex-1" data-testid="nova-proactive-invite-title">👋 Une question ?</p>
            <button
              onClick={dismissProactiveInvite}
              className="text-[#6B6B6E] hover:text-white flex-shrink-0"
              aria-label="Fermer"
              data-testid="nova-proactive-invite-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-sm text-[#B8B8BA] mb-3" data-testid="nova-proactive-invite-message">
            Je peux vous aider à choisir votre abonnement, vous expliquer les offres, les tarifs, les appareils compatibles, les promotions en cours et répondre à toutes vos questions.
          </p>
          <button
            onClick={openFromProactiveInvite}
            className="w-full text-sm px-3 py-2.5 rounded-lg bg-gradient-to-r from-[#00FFFF] to-[#00CFCF] text-[#0A0A0B] font-semibold hover:shadow-lg hover:shadow-[#00FFFF]/30 transition-shadow"
            data-testid="nova-proactive-invite-cta"
          >
            Discuter avec Nova
          </button>
        </div>
      )}

      {/* Notification proactive « installation non terminée » / campagne Waiting>7j */}
      {!open && setupPrompt && (
        <div className="fixed bottom-20 right-5 z-[60] w-[320px] rounded-2xl bg-[#121214] border border-[#FF6B35]/40 shadow-xl shadow-black/40 p-4" data-testid="nova-setup-prompt">
          <div className="flex items-start gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-[#FF6B35] mt-0.5 flex-shrink-0" />
            <p className="text-sm text-[#E8E8E8]" data-testid="nova-setup-prompt-message">{setupPrompt.prompt_message || "Votre abonnement est actif. Besoin d'aide pour l'installer sur votre appareil ?"}</p>
            <button onClick={() => dismissPrompt('later')} className="text-[#6B6B6E] hover:text-white flex-shrink-0" aria-label="Fermer" data-testid="nova-setup-prompt-close"><X className="h-4 w-4" /></button>
          </div>
          {setupPrompt.campaign_intro && (
            <p className="text-xs text-[#B8B8BA] mb-2" data-testid="nova-setup-prompt-intro">{setupPrompt.campaign_intro}</p>
          )}
          <div className="flex flex-col gap-2 mt-2">
            <button onClick={() => openWidget(true)} className="text-xs px-3 py-2 rounded-lg bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-medium" data-testid="nova-setup-yes">{setupPrompt.campaign ? 'Installer maintenant' : "Oui, m'aider à l'installer"}</button>
            <div className="flex gap-2">
              <button onClick={() => dismissPrompt('later')} className="flex-1 text-xs px-3 py-2 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-[#B8B8BA] hover:text-white" data-testid="nova-setup-later">Plus tard</button>
              <button onClick={() => dismissPrompt('already_configured')} className="flex-1 text-xs px-3 py-2 rounded-lg bg-[#1A1A1D] border border-[#3A3A3D] text-[#B8B8BA] hover:text-white" data-testid="nova-setup-done">J'ai déjà configuré</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulle flottante */}
      {!open && (
        <button
          onClick={() => openWidget(false)}
          className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white pl-2 pr-4 py-2 shadow-lg shadow-[#FF6B35]/40 hover:shadow-xl hover:shadow-[#FF6B35]/50 transition-all"
          aria-label="Ouvrir l'assistante Nova"
          data-testid="nova-bubble"
        >
          <img src={NOVA_AVATAR} alt="Nova" className="w-8 h-8 rounded-full object-cover border border-white/40" />
          <span className="text-sm font-semibold">Besoin d'aide ?</span>
        </button>
      )}

      {/* Panneau de chat */}
      {mounted && (
        <div
          className={`fixed z-[60] bg-[#0A0A0B] border border-[#3A3A3D] shadow-2xl overflow-hidden flex flex-col transition-opacity ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}
            inset-0 rounded-none
            sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[400px] sm:h-[620px] sm:max-h-[85vh] sm:rounded-2xl`}
          data-testid="nova-widget-panel"
        >
          {/* Barre de contrôle du widget */}
          <div className="flex items-center justify-between px-3 py-2 bg-[#111113] border-b border-[#3A3A3D]">
            <div className="flex items-center gap-2">
              <img src={NOVA_AVATAR} alt="Nova" className="w-6 h-6 rounded-full object-cover border border-[#FF6B35]/40" />
              <span className="text-white text-sm font-semibold">Nova</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={minimize} className="p-1.5 rounded-md text-[#B8B8BA] hover:text-white hover:bg-[#1A1A1D] transition-colors" aria-label="Réduire" data-testid="nova-minimize">
                <Minus className="h-4 w-4" />
              </button>
              <button onClick={close} className="p-1.5 rounded-md text-[#B8B8BA] hover:text-white hover:bg-[#1A1A1D] transition-colors" aria-label="Fermer" data-testid="nova-close">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Chat (chargé à l'ouverture uniquement) */}
          <div className="flex-1 min-h-0">
            <Suspense fallback={<div className="h-full flex items-center justify-center"><Loader2 className="h-6 w-6 text-[#FF6B35] animate-spin" /></div>}>
              <AssistantChat
                key={resumeKey}
                compact
                pageContext={setupMode ? { route: location.pathname, page_type: 'setup_iptv', intent: 'setup_iptv', campaign_source: campaignSource } : pageContext}
                onNavigate={handleAfterNavigate}
                initialQuestion={setupMode ? '' : initialQuestion}
                initialAssistant={setupMode ? SETUP_WELCOME : null}
              />
            </Suspense>
          </div>
        </div>
      )}
    </>
  );
};

export default NovaFloatingWidget;
